/** Labour & SPLH module — sales per labour hour, labour % heatmap, drilldown. */
import React from 'react';
import { DollarSign, Percent, Trophy, Users } from 'lucide-react';
import KpiTile from './KpiTile';
import Heatmap from './Heatmap';
import StoreCompareList from './StoreCompareList';
import CoryTips from '@/components/demo/CoryTips';
import { QSR_STORES, DAYPARTS, TARGETS, avgBy } from './qsrDemoData';

export default function LabourSplhModule({ onSelectStore, onNavigate }) {
  const netSplh = avgBy('splh');
  const netLabour = avgBy('labourPct');
  const best = [...QSR_STORES].sort((a, b) => b.splh - a.splh)[0];
  const heat = QSR_STORES.map((s) => s.labourByDaypart);

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="labour-module">
      <CoryTips page="operations" variant="qsr" onAction={onNavigate} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={DollarSign} label="Network SPLH" value={`$${netSplh.toFixed(1)}`} sub={`target $${TARGETS.splh}`} delta={`$${(netSplh - TARGETS.splh).toFixed(1)}`} deltaGood={netSplh >= TARGETS.splh} color="#10b981" trend={[68, 69, 70, 70, 71, 71, 72, 72, 73, 73, 74, netSplh]} testid="labour-kpi-splh" source="Tanda + Lightspeed · vs $75 industry benchmark" ai={`Network SPLH is $${netSplh.toFixed(1)} vs a $${TARGETS.splh} target. Footscray shows it's achievable — cloning its dinner roster to Southport is the biggest lever.`} />
        <KpiTile icon={Percent} label="Labour cost %" source="Tanda · vs 27% QSR benchmark" value={`${netLabour.toFixed(1)}%`} sub={`target ${TARGETS.labourPct}%`} delta={`${(netLabour - TARGETS.labourPct).toFixed(1)}%`} deltaGood={netLabour <= TARGETS.labourPct} color="#f59e0b" />
        <KpiTile icon={Trophy} label="Top store SPLH" source="Tanda + Lightspeed" value={`$${best.splh.toFixed(1)}`} sub={best.name} color="#8b5cf6" />
        <KpiTile icon={Users} label="Rostered hours / wk" source="Tanda roster" value="1,842" sub="across 3 stores" color="#3b82f6" />
      </div>

      <Heatmap
        title="Labour cost % by day-part (lower is better)"
        rows={QSR_STORES.map((s) => s.name)}
        cols={DAYPARTS}
        values={heat}
        unit="%"
        invert
        ai="Labour % spikes at dinner in every store but SPLH still lags at Southport — the issue is roster shape, not headcount. Shift crew from the quiet arvo into the dinner peak."
      />

      <div>
        <div className="text-sm font-semibold text-slate-900 mb-2 px-1">Ranked by SPLH — tap for the full profile</div>
        <StoreCompareList
          stores={QSR_STORES}
          sortKey="splh"
          sortDir="desc"
          trendKey="splhTrend"
          trendColor="#10b981"
          onSelect={onSelectStore}
          columns={[
            { label: 'SPLH', fmt: (s) => `$${s.splh.toFixed(1)}` },
            { label: 'Labour', fmt: (s) => `${s.labourPct.toFixed(1)}%` },
          ]}
        />
      </div>
    </div>
  );
}
